import React from 'react';
import { BlockButtonStyle, BlockStyle, LandingBlock } from '../../types';
import { BUTTON_ICONS, FONT_WEIGHTS } from '../../data/fonts';
import { Input } from '../ui/Input';
import { Select } from '../ui/Select';
import { Switch } from '../ui/Switch';
interface StyleSettingsProps {
  block: LandingBlock;
  onChange: (block: LandingBlock) => void;
}
const DEFAULT_STYLE: BlockStyle = {
  background: 'transparente',
  align: 'center',
  shadow: 'ninguna',
  maxWidth: 'normal'
};

/** Deslizador con valor numérico visible. */
function Slider({
  id,
  label,
  min,
  max,
  step = 1,
  suffix = 'px',
  value,
  onChange
}: {
  id: string;
  label: string;
  min: number;
  max: number;
  step?: number;
  suffix?: string;
  value: number | undefined;
  onChange: (value: number) => void;
}) {
  return <div data-id="element-298" data-mp="17oluzau564h8">
      <div className="mb-1.5 flex items-center justify-between" data-id="element-299" data-mp="17nswsy990nap">
        <label htmlFor={id} className="text-sm font-medium text-foreground" data-id="element-300" data-mp="1cfc1ireb0ddi">
          {label}
        </label>
        <span className="text-xs tabular-nums text-muted-foreground" data-id="element-301" data-mp="2sny8mayld5kt">
          {value === undefined ? 'Auto' : `${value}${suffix}`}
        </span>
      </div>
      <input id={id} type="range" min={min} max={max} step={step} value={value ?? min} onChange={e => onChange(Number(e.target.value))} className="h-1.5 w-full cursor-pointer appearance-none rounded-full bg-muted accent-primary" data-id="element-302" data-mp="2nnbkyt9hgc7w" />
    </div>;
}

/** Editor visual de estilo, tipografía y botón de un bloque. */
export function StyleSettings({
  block,
  onChange
}: StyleSettingsProps) {
  const style: BlockStyle = {
    ...DEFAULT_STYLE,
    ...(block.style ?? {})
  };
  const button: BlockButtonStyle = block.button ?? {};
  function setStyle(partial: Partial<BlockStyle>) {
    onChange({
      ...block,
      style: {
        ...style,
        ...partial
      }
    });
  }
  function setButton(partial: Partial<BlockButtonStyle>) {
    onChange({
      ...block,
      button: {
        ...button,
        ...partial
      }
    });
  }
  return <div className="space-y-6" data-id="element-303" data-mp="3m329846yn47r">
      <section className="space-y-4" data-id="element-304" data-mp="3m3v8yl7i4tge">
        <h4 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground" data-id="element-305" data-mp="3r1dee1mb8qjn">
          Fondo y caja
        </h4>
        <Select id={`${block.id}-bg`} label="Fondo" options={[{
        value: 'transparente',
        label: 'Transparente'
      }, {
        value: 'blanco',
        label: 'Blanco'
      }, {
        value: 'gris',
        label: 'Gris claro'
      }, {
        value: 'marca',
        label: 'Color de marca'
      }, {
        value: 'oscuro',
        label: 'Oscuro'
      }]} value={style.background ?? 'transparente'} onChange={e => setStyle({
        background: e.target.value as BlockStyle['background']
      })} data-id="element-306" data-mp="3v2p74g0hdrw2" />
        <Slider id={`${block.id}-py`} label="Espaciado vertical" min={0} max={160} step={4} value={style.paddingY} onChange={paddingY => setStyle({
        paddingY
      })} data-id="element-307" data-mp="mmncy727d72r" />
        <Slider id={`${block.id}-px`} label="Espaciado horizontal" min={0} max={120} step={4} value={style.paddingX} onChange={paddingX => setStyle({
        paddingX
      })} data-id="element-308" data-mp="wmeayipz247l" />
        <Slider id={`${block.id}-radius`} label="Radio de bordes" min={0} max={48} value={style.radius} onChange={radius => setStyle({
        radius
      })} data-id="element-309" data-mp="11pnhh8lnue2m" />
        <Select id={`${block.id}-shadow`} label="Sombra" options={[{
        value: 'ninguna',
        label: 'Sin sombra'
      }, {
        value: 'suave',
        label: 'Suave'
      }, {
        value: 'media',
        label: 'Media'
      }, {
        value: 'fuerte',
        label: 'Fuerte'
      }]} value={style.shadow ?? 'ninguna'} onChange={e => setStyle({
        shadow: e.target.value as BlockStyle['shadow']
      })} data-id="element-310" data-mp="wt6l5r4b4teg" />
      </section>

      <section className="space-y-4 border-t border-border pt-5" data-id="element-311" data-mp="1bnbek56qnxny">
        <h4 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground" data-id="element-312" data-mp="2860lckb2rvio">
          Tipografía
        </h4>
        <Select id={`${block.id}-align`} label="Alineación" options={[{
        value: 'left',
        label: 'Izquierda'
      }, {
        value: 'center',
        label: 'Centro'
      }, {
        value: 'right',
        label: 'Derecha'
      }]} value={style.align ?? 'center'} onChange={e => setStyle({
        align: e.target.value as BlockStyle['align']
      })} data-id="element-313" data-mp="23fajhh1uyasq" />
        <Select id={`${block.id}-weight`} label="Grosor" placeholder="Automático" options={FONT_WEIGHTS} value={style.titleWeight ? String(style.titleWeight) : ''} onChange={e => setStyle({
        titleWeight: e.target.value ? Number(e.target.value) : undefined
      })} data-id="element-314" data-mp="1iy69ai2hv0w9" />
        <Slider id={`${block.id}-tracking`} label="Espaciado entre letras" min={-2} max={8} step={0.5} value={style.letterSpacing} onChange={letterSpacing => setStyle({
        letterSpacing
      })} data-id="element-315" data-mp="2boy358w37dgf" />
        <Input id={`${block.id}-color`} label="Color del texto" type="color" value={style.textColor ?? '#0B0B0F'} onChange={e => setStyle({
        textColor: e.target.value
      })} data-id="element-316" data-mp="21uxrspa7pbz9" />
        <Switch id={`${block.id}-upper`} label="Texto en mayúsculas" checked={Boolean(style.uppercase)} onChange={uppercase => setStyle({
        uppercase
      })} data-id="element-317" data-mp="2ufpjiuehs44b" />
      </section>

      <section className="space-y-4 border-t border-border pt-5" data-id="element-318" data-mp="dyzwsetxmdsb">
        <h4 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground" data-id="element-319" data-mp="sop46rsmbe11">
          Botón
        </h4>
        <Input id={`${block.id}-btn-label`} label="Texto del botón" placeholder="Usar el texto del contenido" value={button.label ?? ''} onChange={e => setButton({
        label: e.target.value
      })} data-id="element-320" data-mp="nqnmobaubmro" />
        <div className="grid grid-cols-2 gap-3" data-id="element-321" data-mp="3r7m8oqxudl8s">
          <Input id={`${block.id}-btn-color`} label="Color" type="color" value={button.color ?? '#3D8BFF'} onChange={e => setButton({
          color: e.target.value
        })} data-id="element-322" data-mp="9xo8ccmsnylp" />
          <Input id={`${block.id}-btn-hover`} label="Color hover" type="color" value={button.hoverColor ?? '#2F6FD1'} onChange={e => setButton({
          hoverColor: e.target.value
        })} data-id="element-323" data-mp="517f540qmu1r" />
          <Input id={`${block.id}-btn-text`} label="Color del texto" type="color" value={button.textColor ?? '#FFFFFF'} onChange={e => setButton({
          textColor: e.target.value
        })} data-id="element-324" data-mp="1ggckkfs976p2" />
          <Select id={`${block.id}-btn-icon`} label="Icono" options={BUTTON_ICONS} value={button.icon ?? ''} onChange={e => setButton({
          icon: e.target.value
        })} data-id="element-325" data-mp="1si46bctyomq3" />
        </div>
        <Slider id={`${block.id}-btn-radius`} label="Radio de bordes" min={0} max={40} value={button.radius} onChange={radius => setButton({
        radius
      })} data-id="element-326" data-mp="22ce81l4smxdh" />
        <Slider id={`${block.id}-btn-height`} label="Altura" min={36} max={80} value={button.height} onChange={height => setButton({
        height
      })} data-id="element-327" data-mp="19ql61drbnmrr" />
        <Slider id={`${block.id}-btn-padding`} label="Padding horizontal" min={12} max={80} value={button.paddingX} onChange={paddingX => setButton({
        paddingX
      })} data-id="element-328" data-mp="1jfcuvd135qyg" />
        <Select id={`${block.id}-btn-anim`} label="Animación" options={[{
        value: 'ninguna',
        label: 'Sin animación'
      }, {
        value: 'elevar',
        label: 'Elevar al pasar'
      }, {
        value: 'escalar',
        label: 'Escalar al pasar'
      }, {
        value: 'pulso',
        label: 'Pulso'
      }]} value={button.animation ?? 'elevar'} onChange={e => setButton({
        animation: e.target.value as BlockButtonStyle['animation']
      })} data-id="element-329" data-mp="lyeauhxc1ltx" />
        <Switch id={`${block.id}-btn-full`} label="Ancho completo" checked={Boolean(button.fullWidth)} onChange={fullWidth => setButton({
        fullWidth
      })} data-id="element-330" data-mp="2qbws8x7h1vrz" />
        <Switch id={`${block.id}-btn-shadow`} label="Sombra y brillo" checked={button.shadow !== false} onChange={shadow => setButton({
        shadow
      })} data-id="element-331" data-mp="2gj7ic6rkkpyr" />
      </section>
    </div>;
}